
// src/Views/Main Panel/MenuList/MenuList.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
    Button, Card, Col, Drawer, Form, Input, InputNumber,
    message, Modal, Pagination, Row, Select, Space, Switch,
    Table, Tag, Tooltip, Upload, Badge, Divider,
} from "antd";
import {
    CheckOutlined, CloseOutlined, DeleteOutlined, EditFilled,
    PlusOutlined, ReloadOutlined, SearchOutlined, CameraOutlined,
    FilterOutlined, BarChartOutlined,
} from "@ant-design/icons";
import { dispatchtoast } from "../../../utils";
import errorImage from "../../../assets/images/food.png";
import {
    getMenuList_list,
    setActiveInactive,
    setDeleteMenu,
    setUpdateFoodMenu,
    getCategoryList,
    getVendorsDetails,
    getallrestroList,
    bulkDeleteMenu,
    bulkUpdateMenuStatus,
} from "../../../ApiServices/Apifun";
import AddMenu from "./AddMenu";
import "./MenuList.css";

const { Option } = Select;

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────
const getFirstImageUrl = (foodImage) => {
    if (!foodImage) return null;
    if (typeof foodImage === "string") return foodImage;
    if (Array.isArray(foodImage) && foodImage.length > 0) {
        const first = foodImage[0];
        return typeof first === "string" ? first : first?.url || null;
    }
    return null;
};

// ────────────────────────────────────────────────────────
// Stat Card
// ────────────────────────────────────────────────────────
const StatCard = ({ icon, value, label, color }) => (
    <div className="stat-card">
        <div className="stat-icon" style={{ background: color + "20" }}>
            <span style={{ color }}>{icon}</span>
        </div>
        <div>
            <div className="stat-value">{value}</div>
            <div className="stat-label">{label}</div>
        </div>
    </div>
);

// ────────────────────────────────────────────────────────
// MenuList
// ────────────────────────────────────────────────────────
const MenuList = () => {
    // ── State ──────────────────────────────────────────────
    const [foodData, setFoodData] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [openEditDrawer, setOpenEditDrawer] = useState(false);
    const [formEdit] = Form.useForm();
    const [editImageFiles, setEditImageFiles] = useState([]);
    const [addLoader, setAddLoader] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [editId, setEditId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState(undefined);
    const [selectedStatus, setSelectedStatus] = useState(undefined);
    const [selectedVendor, setSelectedVendor] = useState(undefined);
    const [selectedRestaurant, setSelectedRestaurant] = useState(undefined);
    const [minPrice, setMinPrice] = useState(undefined);
    const [maxPrice, setMaxPrice] = useState(undefined);
    const [categories, setCategories] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loadingCat, setLoadingCat] = useState(false);
    const [loadingVen, setLoadingVen] = useState(false);
    const [loadingRest, setLoadingRest] = useState(false);
    const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
    const [showFilters, setShowFilters] = useState(false);

    // ── Fetch reference data ────────────────────────────────
    useEffect(() => {
        fetchCategories();
        fetchVendors();
        fetchRestaurants();
    }, []);

    const fetchCategories = async () => {
        setLoadingCat(true);
        try {
            const res = await getCategoryList();
            if (res?.status === "success") setCategories(res.data?.categories || []);
        } catch { /* silent */ }
        finally { setLoadingCat(false); }
    };

    const fetchVendors = async () => {
        setLoadingVen(true);
        try {
            const res = await getVendorsDetails();
            if (res?.status === "success") setVendors(res.data?.vendor || []);
        } catch { /* silent */ }
        finally { setLoadingVen(false); }
    };

    const fetchRestaurants = async () => {
        setLoadingRest(true);
        try {
            const res = await getallrestroList();
            if (res?.status === "success")
                setRestaurants(res.data?.restaurants || res.data || []);
        } catch { /* silent */ }
        finally { setLoadingRest(false); }
    };

    // ── Fetch menu list ─────────────────────────────────────
    const FetchMenuList = useCallback(
        async (page = currentPage) => {
            setIsLoading(true);
            try {
                let url = `?page=${page}&limit=${pageSize}`;
                if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
                if (selectedCategory !== undefined)
                    url += `&categoryId=${encodeURIComponent(selectedCategory)}`;
                if (selectedStatus !== undefined) url += `&isAvailable=${selectedStatus}`;
                if (selectedVendor !== undefined) url += `&vendorId=${selectedVendor}`;
                if (selectedRestaurant !== undefined)
                    url += `&restaurantId=${selectedRestaurant}`;
                if (minPrice !== undefined) url += `&minPrice=${minPrice}`;
                if (maxPrice !== undefined) url += `&maxPrice=${maxPrice}`;

                const res = await getMenuList_list(url);
                if (res?.status === "success") {
                    const items = res.data?.foodItems || [];
                    setFoodData(items);
                    setTotalItems(res.data?.pagination?.totalItems || 0);
                    setCurrentPage(res.data?.pagination?.currentPage || 1);
                    // Derive stats
                    setStats({
                        total: res.data?.pagination?.totalItems || items.length,
                        active: items.filter((i) => i.isAvailable === 1 || i.isActive === 1)
                            .length,
                        inactive: items.filter(
                            (i) => i.isAvailable !== 1 && i.isActive !== 1
                        ).length,
                    });
                } else {
                    message.error(res?.message || "Failed to fetch menu items");
                }
            } catch (err) {
                console.error(err);
                message.error("Failed to fetch menu items");
            } finally {
                setIsLoading(false);
            }
        },
        // eslint-disable-next-line
        [
            currentPage, pageSize, searchTerm, selectedCategory,
            selectedStatus, selectedVendor, selectedRestaurant, minPrice, maxPrice,
        ]
    );

    useEffect(() => {
        FetchMenuList(currentPage);
        // eslint-disable-next-line
    }, [
        currentPage, pageSize, searchTerm, selectedCategory,
        selectedStatus, selectedVendor, selectedRestaurant, minPrice, maxPrice,
    ]);

    // ── Filters ─────────────────────────────────────────────
    const resetFilters = () => {
        setSearchTerm("");
        setSelectedCategory(undefined);
        setSelectedStatus(undefined);
        setSelectedVendor(undefined);
        setSelectedRestaurant(undefined);
        setMinPrice(undefined);
        setMaxPrice(undefined);
        setCurrentPage(1);
    };

    // ── Toggle status ────────────────────────────────────────
    const handleToggleStatus = (id, currentStatus) => {
        const isActive = currentStatus === 1;
        Modal.confirm({
            title: isActive ? "Deactivate Item?" : "Activate Item?",
            content: `This menu item will be ${isActive ? "hidden from" : "visible on"} the app.`,
            okText: isActive ? "Deactivate" : "Activate",
            okType: isActive ? "danger" : "primary",
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    const res = await setActiveInactive(id);
                    if (res?.status) {
                        message.success(
                            `Item ${isActive ? "deactivated" : "activated"} successfully`
                        );
                        FetchMenuList(currentPage);
                    } else {
                        message.error(res?.message || "Failed to update status");
                    }
                } catch {
                    message.error("An error occurred");
                }
            },
        });
    };

    // ── Delete ───────────────────────────────────────────────
    const handleDelete = (id, name) => {
        Modal.confirm({
            title: "Delete Menu Item",
            content: (
                <span>
                    Delete <strong>"{name}"</strong>? This cannot be undone.
                </span>
            ),
            okText: "Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    const res = await setDeleteMenu(id);
                    if (res?.status) {
                        message.success("Item deleted");
                        FetchMenuList(currentPage);
                    } else {
                        message.error(res?.message || "Failed to delete");
                    }
                } catch {
                    message.error("An error occurred");
                }
            },
        });
    };

    // ── Bulk actions ─────────────────────────────────────────
    const handleBulkDelete = () => {
        if (!selectedRowKeys.length) return message.warning("Select items first");
        Modal.confirm({
            title: "Bulk Delete",
            content: `Delete ${selectedRowKeys.length} selected items?`,
            okText: "Delete All",
            okType: "danger",
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    const res = await bulkDeleteMenu({ ids: selectedRowKeys });
                    if (res?.status) {
                        message.success(`${selectedRowKeys.length} items deleted`);
                        setSelectedRowKeys([]);
                        FetchMenuList(currentPage);
                    }
                } catch {
                    message.error("Bulk delete failed");
                }
            },
        });
    };

    const handleBulkStatus = (status) => {
        if (!selectedRowKeys.length) return message.warning("Select items first");
        const label = status === 1 ? "activate" : "deactivate";
        Modal.confirm({
            title: `Bulk ${status === 1 ? "Activate" : "Deactivate"}`,
            content: `${status === 1 ? "Activate" : "Deactivate"
                } ${selectedRowKeys.length} items?`,
            okText: status === 1 ? "Activate" : "Deactivate",
            okType: status === 1 ? "primary" : "danger",
            cancelText: "Cancel",
            onOk: async () => {
                try {
                    const res = await bulkUpdateMenuStatus({
                        ids: selectedRowKeys,
                        isAvailable: status,
                    });
                    if (res?.status) {
                        message.success(`${selectedRowKeys.length} items updated`);
                        setSelectedRowKeys([]);
                        FetchMenuList(currentPage);
                    }
                } catch {
                    message.error(`Bulk ${label} failed`);
                }
            },
        });
    };

    // ── Open edit drawer ─────────────────────────────────────
    const handleEdit = (record) => {
        setEditId(record.id);
        formEdit.setFieldsValue({
            name: record.name,
            categoryId: record.Category?.id || record.categoryId,
            price: record.price,
            description: record.description,
            gstTax: record.gstTax,
            cgst_tax: record.cgst_tax,
            sgst_tax: record.sgst_tax,
            isAvailable: record.isAvailable === 1 || record.isActive === 1,
        });

        const imgUrl = getFirstImageUrl(record.foodImage || record.food_images);
        if (imgUrl) {
            setEditImageFiles([
                { uid: "-1", name: "current", status: "done", url: imgUrl },
            ]);
        } else {
            setEditImageFiles([]);
        }

        setOpenEditDrawer(true);
    };

    // ── Submit edit ──────────────────────────────────────────
    const handleEditMenu = async (values) => {
        const formData = new FormData();
        formData.append("name", values.name);
        formData.append("price", values.price);
        formData.append("description", values.description || "");
        formData.append("gstTax", values.gstTax || 0);
        formData.append("cgst_tax", values.cgst_tax || 0);
        formData.append("sgst_tax", values.sgst_tax || 0);
        formData.append("isAvailable", values.isAvailable ? "1" : "0");
        if (values.categoryId) formData.append("categoryId", values.categoryId);

        editImageFiles.forEach((f) => {
            if (f.originFileObj) formData.append("food_images", f.originFileObj);
        });

        setAddLoader(true);
        try {
            const res = await setUpdateFoodMenu(formData, editId);
            if (res?.status) {
                dispatchtoast(res.message || "Menu item updated successfully!");
                setOpenEditDrawer(false);
                setEditImageFiles([]);
                formEdit.resetFields();
                FetchMenuList(currentPage);
            } else {
                message.error(res?.message || "Failed to update menu");
            }
        } catch (err) {
            console.error(err);
            message.error("An error occurred");
        } finally {
            setAddLoader(false);
        }
    };

    // ── Edit upload config ───────────────────────────────────
    const editUploadProps = {
        multiple: false,
        maxCount: 1,
        listType: "picture-card",
        fileList: editImageFiles,
        beforeUpload: (file) => {
            if (!file.type.startsWith("image/")) {
                message.error("Only image files allowed");
                return Upload.LIST_IGNORE;
            }
            const reader = new FileReader();
            reader.onload = (e) => {
                setEditImageFiles([
                    {
                        uid: file.uid,
                        name: file.name,
                        status: "done",
                        originFileObj: file,
                        url: e.target.result,
                    },
                ]);
            };
            reader.readAsDataURL(file);
            return false;
        },
        onRemove: () => setEditImageFiles([]),
    };

    // ── Table columns ────────────────────────────────────────
    const columns = [
        {
            title: "Image",
            dataIndex: "foodImage",
            key: "image",
            width: 74,
            render: (foodImage, record) => {
                const url = getFirstImageUrl(
                    foodImage || record.food_images
                );
                return url ? (
                    <img
                        src={url}
                        alt={record.name}
                        className="food-img-thumb"
                        onError={(e) => {
                            e.target.src = errorImage;
                        }}
                        crossOrigin="anonymous"
                    />
                ) : (
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center border border-blue-100">
                        <CameraOutlined className="text-blue-300 text-lg" />
                    </div>
                );
            },
        },
        {
            title: "Food Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
            render: (text, record) => (
                <div className="flex items-start gap-2">
                    <span
                        className="veg-dot mt-1"
                        style={{
                            background:
                                record.isVeg === 1 || record.isVeg === true
                                    ? "#16a34a"
                                    : "#dc2626",
                        }}
                    />
                    <div>
                        <div className="font-semibold text-gray-800 leading-tight">
                            {text || "--"}
                        </div>
                        {record.description && (
                            <div className="text-xs text-gray-400 mt-0.5 max-w-[180px] truncate">
                                {record.description}
                            </div>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: "Category",
            dataIndex: "Category",
            key: "category",
            render: (cat) => (
                <Tag color="geekblue" className="rounded-full text-xs font-medium">
                    {cat?.categoryName || "Uncategorized"}
                </Tag>
            ),
        },
        {
            title: "Price",
            dataIndex: "price",
            key: "price",
            sorter: (a, b) => (a.price || 0) - (b.price || 0),
            render: (price) => (
                <span className="price-cell">₹{price ?? 0}</span>
            ),
        },
        {
            title: "GST",
            dataIndex: "gstTax",
            key: "gst",
            width: 70,
            render: (tax) =>
                tax ? (
                    <span className="text-xs text-gray-500 font-medium">{tax}%</span>
                ) : (
                    <span className="text-xs text-gray-300">—</span>
                ),
        },
        {
            title: "Status",
            dataIndex: "isAvailable",
            key: "status",
            width: 110,
            render: (isAvailable, record) => {
                const active = record.isActive == 1;
                return (
                    <span
                        className={`status-pill ${active ? "active" : "inactive"}`}
                        onClick={() =>
                            handleToggleStatus(
                                record.id,
                                active ? 1 : 0
                            )
                        }
                    >
                        <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ background: active ? "#16a34a" : "#dc2626" }}
                        />
                        {active ? "Active" : "Inactive"}
                    </span>
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => {
                const active = record.isAvailable === 1 || record.isActive === 1;
                return (
                    <Space size={4}>
                        <Tooltip title="Edit">
                            <Button
                                type="text"
                                size="small"
                                className="action-btn hover:bg-blue-50"
                                icon={<EditFilled className="text-blue-500" />}
                                onClick={() => handleEdit(record)}
                            />
                        </Tooltip>
                        {/* <Tooltip title={active ? "Deactivate" : "Activate"}>
                            <Button
                                type="text"
                                size="small"
                                className="action-btn"
                                style={{
                                    "--hover-bg": active ? "#fff7ed" : "#f0fdf4",
                                }}
                                icon={
                                    active ? (
                                        <CloseOutlined className="text-orange-500" />
                                    ) : (
                                        <CheckOutlined className="text-green-500" />
                                    )
                                }
                                onClick={() =>
                                    handleToggleStatus(record.id, active ? 1 : 0)
                                }
                            />
                        </Tooltip> */}
                        <Tooltip title="Delete">
                            <Button
                                type="text"
                                size="small"
                                className="action-btn hover:bg-red-50"
                                icon={<DeleteOutlined className="text-red-400" />}
                                onClick={() => handleDelete(record.id, record.name)}
                                danger
                            />
                        </Tooltip>
                    </Space>
                );
            },
        },
    ];

    // ── Row selection ────────────────────────────────────────
    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
        selections: [
            Table.SELECTION_ALL,
            Table.SELECTION_INVERT,
            Table.SELECTION_NONE,
        ],
    };

    // ────────────────────────────────────────────────────────
    // Render
    // ────────────────────────────────────────────────────────
    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">

            {/* ── Page Header ─────────────────────────────── */}
            <div className="page-header">
                <div>
                    <h1>🍽️ Food Gallery</h1>
                    <p>Manage your restaurant menu — add, edit, and control availability</p>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setOpenModal(true)}
                    size="large"
                    className="!bg-white !text-blue-700 !border-blue-200 hover:!bg-blue-50 font-semibold shadow-none"
                >
                    Add Food Item
                </Button>
            </div>

            {/* ── Stats ───────────────────────────────────── */}
            <Row gutter={[12, 12]} className="mb-4">
                <Col xs={12} sm={8} lg={4}>
                    <StatCard
                        icon={<BarChartOutlined />}
                        value={totalItems}
                        label="Total Items"
                        color="#2563eb"
                    />
                </Col>
                <Col xs={12} sm={8} lg={4}>
                    <StatCard
                        icon={<CheckOutlined />}
                        value={stats.active}
                        label="Active"
                        color="#16a34a"
                    />
                </Col>
                <Col xs={12} sm={8} lg={4}>
                    <StatCard
                        icon={<CloseOutlined />}
                        value={stats.inactive}
                        label="Inactive"
                        color="#dc2626"
                    />
                </Col>
            </Row>

            {/* ── Filters ─────────────────────────────────── */}
            <Card
                className="filter-card mb-4 shadow-sm rounded-xl"
                bodyStyle={{ padding: "14px 20px" }}
            >
                <div className="flex flex-wrap gap-3 items-center">
                    <Input
                        placeholder="Search by name…"
                        prefix={<SearchOutlined className="text-gray-400" />}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        allowClear
                        className="w-52"
                    />

                    <Select
                        placeholder="Category"
                        value={selectedCategory}
                        onChange={(v) => {
                            setSelectedCategory(v);
                            setCurrentPage(1);
                        }}
                        allowClear
                        loading={loadingCat}
                        className="w-40"
                        showSearch
                        optionFilterProp="children"
                    >
                        {categories.map((c) => (
                            <Option key={c.id} value={c.id}>
                                {c.categoryName}
                            </Option>
                        ))}
                    </Select>

                    <Select
                        placeholder="Status"
                        value={selectedStatus}
                        onChange={(v) => {
                            setSelectedStatus(v);
                            setCurrentPage(1);
                        }}
                        allowClear
                        className="w-32"
                    >
                        <Option value={1}>Active</Option>
                        <Option value={0}>Inactive</Option>
                    </Select>

                    <Button
                        icon={<FilterOutlined />}
                        onClick={() => setShowFilters((f) => !f)}
                        type={showFilters ? "primary" : "default"}
                    >
                        More filters
                    </Button>

                    <Button onClick={resetFilters}>Reset</Button>

                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={() => FetchMenuList(1)}
                        className="bg-blue-600 ml-auto"
                    >
                        Refresh
                    </Button>
                </div>

                {showFilters && (
                    <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-100">
                        <Select
                            placeholder="Vendor"
                            value={selectedVendor}
                            onChange={(v) => {
                                setSelectedVendor(v);
                                setCurrentPage(1);
                            }}
                            allowClear
                            loading={loadingVen}
                            className="w-44"
                            showSearch
                            optionFilterProp="children"
                        >
                            {vendors.map((v) => (
                                <Option key={v.id} value={v.id}>
                                    {v.name}
                                </Option>
                            ))}
                        </Select>

                        <Select
                            placeholder="Restaurant"
                            value={selectedRestaurant}
                            onChange={(v) => {
                                setSelectedRestaurant(v);
                                setCurrentPage(1);
                            }}
                            allowClear
                            loading={loadingRest}
                            className="w-48"
                            showSearch
                            optionFilterProp="children"
                        >
                            {restaurants.map((r) => (
                                <Option key={r.id} value={r.id}>
                                    {r.name || r.restaurantName}
                                </Option>
                            ))}
                        </Select>

                        <div className="flex items-center gap-2">
                            <InputNumber
                                placeholder="Min ₹"
                                value={minPrice}
                                onChange={(v) => {
                                    setMinPrice(v);
                                    setCurrentPage(1);
                                }}
                                min={0}
                                className="w-24"
                                prefix="₹"
                            />
                            <span className="text-gray-400">–</span>
                            <InputNumber
                                placeholder="Max ₹"
                                value={maxPrice}
                                onChange={(v) => {
                                    setMaxPrice(v);
                                    setCurrentPage(1);
                                }}
                                min={0}
                                className="w-24"
                                prefix="₹"
                            />
                        </div>
                    </div>
                )}
            </Card>

            {/* ── Bulk action bar ──────────────────────────── */}
            {selectedRowKeys.length > 0 && (
                <div className="bulk-bar">
                    <span className="font-semibold text-blue-700 text-sm">
                        <Badge
                            count={selectedRowKeys.length}
                            className="mr-2"
                            style={{ background: "#2563eb" }}
                        />
                        items selected
                    </span>
                    <Space size={8} wrap>
                        <Button
                            size="small"
                            type="primary"
                            ghost
                            icon={<CheckOutlined />}
                            onClick={() => handleBulkStatus(1)}
                        >
                            Activate
                        </Button>
                        <Button
                            size="small"
                            danger
                            ghost
                            icon={<CloseOutlined />}
                            onClick={() => handleBulkStatus(0)}
                        >
                            Deactivate
                        </Button>
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={handleBulkDelete}
                        >
                            Delete
                        </Button>
                        <Button size="small" onClick={() => setSelectedRowKeys([])}>
                            Clear
                        </Button>
                    </Space>
                </div>
            )}

            {/* ── Table ───────────────────────────────────── */}
            <Card className="shadow-sm rounded-xl overflow-hidden" bodyStyle={{ padding: 0 }}>
                <Table
                    bordered={false}
                    loading={isLoading}
                    columns={columns}
                    dataSource={foodData}
                    rowKey="id"
                    rowSelection={rowSelection}
                    pagination={false}
                    scroll={{ x: 800 }}
                    size="middle"
                    className="menu-table"
                    locale={{
                        emptyText: (
                            <div className="py-16 text-center">
                                <div className="text-6xl mb-4">🍽️</div>
                                <p className="text-gray-400 text-base font-medium">
                                    No menu items found
                                </p>
                                <p className="text-gray-300 text-sm mb-4">
                                    Try adjusting filters or add your first item
                                </p>
                                <Button
                                    type="primary"
                                    className="bg-blue-600"
                                    icon={<PlusOutlined />}
                                    onClick={() => setOpenModal(true)}
                                >
                                    Add first item
                                </Button>
                            </div>
                        ),
                    }}
                />

                {/* Pagination */}
                <div className="flex flex-col sm:flex-row justify-between items-center px-4 py-3 border-t border-gray-100 gap-3">
                    <span className="text-sm text-gray-400">
                        {totalItems > 0
                            ? `Showing ${(currentPage - 1) * pageSize + 1}–${Math.min(
                                currentPage * pageSize,
                                totalItems
                            )} of ${totalItems} items`
                            : "No items"}
                    </span>
                    <Pagination
                        total={totalItems}
                        current={currentPage}
                        pageSize={pageSize}
                        onChange={(page, size) => {
                            if (size !== pageSize) {
                                setPageSize(size);
                                setCurrentPage(1);
                            } else {
                                setCurrentPage(page);
                            }
                        }}
                        showSizeChanger
                        showQuickJumper
                        pageSizeOptions={["10", "20", "50"]}
                        size="small"
                    />
                </div>
            </Card>

            {/* ── Add Menu Modal ───────────────────────────── */}
            <AddMenu
                isOpen={openModal}
                setIsOpen={setOpenModal}
                FetchMenuList={() => FetchMenuList(1)}
            />

            {/* ── Edit Drawer ──────────────────────────────── */}
            <Drawer
                title="✏️  Edit Menu Item"
                width={440}
                open={openEditDrawer}
                onClose={() => {
                    setOpenEditDrawer(false);
                    setEditImageFiles([]);
                    formEdit.resetFields();
                }}
                destroyOnClose
                className="edit-drawer"
                footer={
                    <div className="flex gap-3 pt-1">
                        <Button
                            size="large"
                            className="flex-1"
                            onClick={() => {
                                setOpenEditDrawer(false);
                                setEditImageFiles([]);
                                formEdit.resetFields();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            loading={addLoader}
                            className="flex-1 bg-blue-600"
                            onClick={() => formEdit.submit()}
                        >
                            Save Changes
                        </Button>
                    </div>
                }
            >
                <Form
                    layout="vertical"
                    form={formEdit}
                    onFinish={handleEditMenu}
                    requiredMark="optional"
                >
                    <Form.Item
                        name="name"
                        label="Food Name"
                        rules={[{ required: true, message: "Enter food name" }]}
                    >
                        <Input placeholder="Food name" size="large" />
                    </Form.Item>

                    <div className="grid grid-cols-2 gap-x-3">
                        <Form.Item
                            name="price"
                            label="Price (₹)"
                            rules={[{ required: true, message: "Enter price" }]}
                        >
                            <InputNumber
                                placeholder="0"
                                size="large"
                                className="w-full"
                                min={0}
                                prefix="₹"
                            />
                        </Form.Item>

                        <Form.Item name="categoryId" label="Category">
                            <Select
                                placeholder="Select"
                                size="large"
                                loading={loadingCat}
                                showSearch
                                optionFilterProp="children"
                            >
                                {categories.map((c) => (
                                    <Option key={c.id} value={c.id}>
                                        {c.categoryName}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    <Form.Item name="description" label="Description">
                        <Input.TextArea
                            placeholder="Description"
                            rows={3}
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>

                    <Divider className="!my-3 text-sm text-gray-400">Tax</Divider>
                    <div className="grid grid-cols-3 gap-x-3">
                        <Form.Item name="gstTax" label="GST (%)">
                            <InputNumber className="w-full" min={0} max={100} placeholder="0" />
                        </Form.Item>
                        <Form.Item name="cgst_tax" label="CGST (%)">
                            <InputNumber className="w-full" min={0} max={100} placeholder="0" />
                        </Form.Item>
                        <Form.Item name="sgst_tax" label="SGST (%)">
                            <InputNumber className="w-full" min={0} max={100} placeholder="0" />
                        </Form.Item>
                    </div>

                    <Form.Item label="Food Image">
                        <Upload {...editUploadProps}>
                            {editImageFiles.length === 0 && (
                                <div className="flex flex-col items-center gap-1 p-3">
                                    <PlusOutlined className="text-blue-400" />
                                    <span className="text-xs text-gray-400">Upload</span>
                                </div>
                            )}
                        </Upload>
                    </Form.Item>

                    <Form.Item
                        name="isAvailable"
                        label="Availability"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren="Available"
                            unCheckedChildren="Unavailable"
                        />
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    );
};

export default MenuList;